import React from 'react';

import { Link } from 'is24-corecss';
import { IconS24ChevronRight24 } from '@is24/cosma-ui-icons';
import ApplicationPackageJpg from './images/documents@2x.jpg';
import ApplicationPackageWebp from './images/documents@2x.webp';

import ProfilePng from './images/profile-on-mobile.png';
import ProfileWebp from './images/profile-on-mobile.webp';
import DocumentPng from './images/document.png';
import DocumentWebp from './images/document.webp';
import DealPng from './images/deal.png';
import DealWebp from './images/deal.webp';
import { Modal } from '../../../../common/display/modal/Modal';
import { Benefit } from '../../../../../@types/benefits';
import { BenefitModalProps } from '../../../../../@types/benefitModalProps';
import { ImageSource } from '../../../../common/display/imageSource/ImageSource';
import { OrderedList } from '../../../../common/display/orderedList/OrderedList';
import { Carousel } from './carousel/Carousel';
import { useGetHasProduct } from '../../../../../customHooks/useGetHasProduct.hook';
import { ProductType } from '../../../../../@types/product';
import { Msg, useIntl } from '@/i18n';
import { PlusBadge } from '@/components/common/display/plusBadge/PlusBadge';

const styles = {
  overviewTab: { maxWidth: 530 },
  bigText: {
    fontSize: '22px',
    lineHeight: '26px',
    marginBottom: '12px',
  },
  text: {
    fontSize: '14px',
    lineHeight: '18px',
    marginBottom: '12px',
  },
};

const OverviewTab = () => {
  const isEntitledPlusRent = useGetHasProduct(ProductType.MieterPlus);

  if (!isEntitledPlusRent) {
    return (
      <div style={styles.overviewTab}>
        <div style={styles.bigText}>
          <p className='margin-bottom-l'>
            <Msg
              id='benefits.applicationPackageMieterPlus.modal.overview.alternateText.bigText'
              defaultMessage='The ImmoScout24 application package contains...'
            />
          </p>
        </div>
        <div style={styles.text}>
          <p className='margin-bottom-m'>
            <Msg
              id='benefits.applicationPackageMieterPlus.modal.overview.alternateText.smallTextOne'
              defaultMessage='The ImmoScout24 application package contains...'
            />
          </p>
          <p className='margin-bottom-m'>
            <Msg
              id='benefits.applicationPackageMieterPlus.modal.overview.alternateText.smallTextTwo'
              defaultMessage='The ImmoScout24 application package contains...'
            />
          </p>
          <p className='margin-bottom-m'>
            <Msg
              id='benefits.applicationPackageMieterPlus.modal.overview.alternateText.smallTextThree'
              defaultMessage='The ImmoScout24 application package contains...'
            />
          </p>
        </div>
        <Link
          href={
            '/meinkonto/premium-mitgliedschaft/?cmp_id=10-051529&cmp_name=residential_profile&cmp_position=residential_benefits&cmp_creative=application_cta'
          }
          className='margin-top-l'
          variant='default'
          rightIcon={<IconS24ChevronRight24 />}
        >
          <Msg
            id='benefits.applicationPackageMieterPlus.modal.linkDiscoverMieterPlus'
            defaultMessage='Discover Mieter Plus....'
          />
        </Link>
      </div>
    );
  }

  return (
    <div style={styles.overviewTab}>
      <div className='font-heading-xsmall-regular padding-bottom-l'>
        <Msg
          id='benefits.applicationPackageMieterPlus.modal.overview.paragraph'
          defaultMessage='The ImmoScout24 application package contains...'
        />
      </div>
      <ul className='list-check list-spacing-s'>
        <li>
          <Msg
            id='benefits.applicationPackageMieterPlus.modal.overview.bullet1'
            defaultMessage='Automatic creation and...'
          />
        </li>
        <li>
          <Msg
            id='benefits.applicationPackageMieterPlus.modal.overview.bullet2'
            defaultMessage='In high-quality design...'
          />
        </li>
        <li>
          <Msg
            id='benefits.applicationPackageMieterPlus.modal.overview.bullet3'
            defaultMessage='Providers trust the...'
          />
        </li>
        <li>
          <Msg
            id='benefits.applicationPackageMieterPlus.modal.overview.bullet4'
            defaultMessage='Exclusive & free...'
            values={{
              MieterPlusLogo: <PlusBadge variant='rent' />,
            }}
          />
        </li>
      </ul>
      <div className='margin-vertical-l'>
        <Carousel />
      </div>
    </div>
  );
};

const HowItWorksTab = () => {
  const { t } = useIntl();

  const Item = OrderedList.Item;

  return (
    <div className='margin-bottom-xxl'>
      <OrderedList>
        <Item
          image={
            <ImageSource
              image={ProfilePng}
              imageWebp={ProfileWebp}
              imageAlt={t({
                id: 'benefits.applicationPackageMieterPlus.modal.howItWorks.image1',
                defaultMessage: 'your profile',
              })}
              sizes='70px'
            />
          }
          text={t({
            id: 'benefits.applicationPackageMieterPlus.modal.howItWorks.paragraph1',
            defaultMessage: 'generate your application package in minutes',
          })}
        />
        <Item
          image={
            <ImageSource
              image={DocumentPng}
              imageWebp={DocumentWebp}
              imageAlt={t({
                id: 'benefits.applicationPackageMieterPlus.modal.howItWorks.image2',
                defaultMessage: 'application package on paper and digital',
              })}
              sizes='70px'
            />
          }
          text={t({
            id: 'benefits.applicationPackageMieterPlus.modal.howItWorks.paragraph2',
            defaultMessage: 'click to generate document...',
          })}
        />
        <Item
          image={
            <ImageSource
              image={DealPng}
              imageWebp={DealWebp}
              imageAlt={String(
                t({
                  id: 'benefits.applicationPackageMieterPlus.modal.howItWorks.image3',
                  defaultMessage: 'contract',
                }),
              )}
              sizes='70px'
            />
          }
          text={t({
            id: 'benefits.applicationPackageMieterPlus.modal.howItWorks.paragraph3',
            defaultMessage: 'download and print...',
          })}
        />
      </OrderedList>
    </div>
  );
};

export const ApplicationPackageMieterPlus = ({ isVisible, onClose }: BenefitModalProps) => {
  const { t } = useIntl();
  const isEntitledPlusRent = useGetHasProduct(ProductType.MieterPlus);
  return (
    <Modal
      title={
        isEntitledPlusRent
          ? t({ id: 'benefits.applicationPackageMieterPlus.headlineOne', defaultMessage: 'Application package' })
          : t({ id: 'benefits.applicationPackageMieterPlus.headlineTwo', defaultMessage: 'Application package' })
      }
      isVisible={isVisible}
      image={ApplicationPackageJpg}
      imageWebp={ApplicationPackageWebp}
      imageMobile={ApplicationPackageJpg}
      imageMobileWebp={ApplicationPackageWebp}
      imageUseFullWidth={true}
      imageAlt={t({
        id: 'benefits.applicationPackageMieterPlus.modal.image',
        defaultMessage: 'Application package is handed over',
      })}
      tabsContent={
        isEntitledPlusRent
          ? [
              <OverviewTab key='benefits.applicationPackageMieterPlus.modal.overview' />,
              <HowItWorksTab key='benefits.applicationPackageMieterPlus.modal.howItWorks' />,
            ]
          : [<OverviewTab key='benefits.applicationPackageMieterPlus.modal.overview' />]
      }
      buttonArea={
        <div className='align-center'>
          <a
            className='button-secondary button-rounded'
            href='/meinkonto/profil/erstellen-myscout/?cmp_id=10-051403&cmp_name=residential_profile&cmp_position=residential_benefits&cmp_creative=application_cta'
          >
            <Msg
              id={
                isEntitledPlusRent
                  ? 'benefits.applicationPackageMieterPlus.modal.linkButtonWithMieterPlus'
                  : 'benefits.applicationPackageMieterPlus.modal.linkButtonWithoutMieterPlus'
              }
              defaultMessage='To my application package'
            />
          </a>
        </div>
      }
      benefit={Benefit.DOCUMENTS}
      onClose={onClose}
    />
  );
};
